import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  CreateApiKeyRequest,
  UpdateApiKeyRequest,
} from "@/features/users/schemas/user";
import {
  createApiKey,
  deleteApiKey,
  getApiKeysByUser,
  regenerateApiKey,
  updateApiKey,
} from "@/features/users/services/apiKeys";

export const apiKeyKeys = {
  all: ["apiKeys"] as const,
  byUser: (userId: string) => [...apiKeyKeys.all, "user", userId] as const,
};

export function useApiKeysByUser(userId: string) {
  return useQuery({
    queryKey: apiKeyKeys.byUser(userId),
    queryFn: () => getApiKeysByUser(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateApiKeyRequest) => createApiKey(data),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: apiKeyKeys.byUser(variables.user),
      });
    },
  });
}

export function useUpdateApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateApiKeyRequest }) =>
      updateApiKey(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: apiKeyKeys.all });
    },
  });
}

export function useRegenerateApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => regenerateApiKey(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: apiKeyKeys.all });
    },
  });
}

export function useDeleteApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteApiKey(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: apiKeyKeys.all });
    },
  });
}
